/* eslint-disable import/extensions */
/* eslint-disable no-unused-vars */
import React, {
  useState, useContext, useEffect, useRef, useMemo
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PAYMENT_STATE, updatePaymentStates } from '@apps/redux/features/PayAndReviewSlice'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import PaymentChangeHandlers, {
  PAYMENT_EVENTS
} from '@apps/utils/ChangeHandlers/PaymentChangeHandlers'
import { CARD_VALIDATION_ERRORS } from '@apps/utils/constants'
import { getAddressSearchDebounceMs } from '@apps/utils/addressSearch'
import { Input, Autocomplete, CardInfo } from '@telus/koodo-ui-kit'

import { CardType, formatCreditCard, formatCreditCardAmex } from './helper'
import BodyError from '../Typography/BodyError'
import BodySmall from '../Typography/BodySmall'
import MarkdownRenderer from '../MarkdownRenderer'

export const dateInPast = (firstDate, secondDate) => {
  if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
    return true
  }
  return false
}

export const validateSecurityCode = (securityCode, cardNumber, expiryRef, textContent, updatePaymentStore) => {
  if (securityCode == null || securityCode === '') {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.SECURITY_CODE_ERROR]: textContent?.shortTextFields?.securityCodeRequired })
    expiryRef.current.scrollIntoView({
      behavior: "smooth"
    })
    return
  }
  if ((!CardType(cardNumber) || CardType(cardNumber) === 'amex') && securityCode.length !== 4) {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.SECURITY_CODE_ERROR]: textContent?.shortTextFields?.securityCodeAmex })
    expiryRef.current.scrollIntoView({
      behavior: "smooth"
    })
    return
  }
  if (CardType(cardNumber) && CardType(cardNumber) !== 'amex' && securityCode.length !== 3) {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.SECURITY_CODE_ERROR]: textContent?.shortTextFields?.securityCodeMC })
    expiryRef.current.scrollIntoView({
      behavior: "smooth"
    })
  }
}

export const validateCardNumber = (cardNumber, ccRef, textContent, updatePaymentStore) => {
  if ((cardNumber == null || cardNumber === '') && ccRef.current) {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER_ERROR]: textContent?.shortTextFields?.cardNumberRequired })
    ccRef.current.scrollIntoView({
      behavior: "smooth"
    })
    return
  }
  if (CardType(cardNumber) && CardType(cardNumber) === 'amex' && cardNumber.length !== 19) {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER_ERROR]: textContent?.shortTextFields?.cardNumberInvalid })
    ccRef.current.scrollIntoView({
      behavior: "smooth"
    })
  }
  if (CardType(cardNumber) && CardType(cardNumber) !== 'amex' && cardNumber.length !== 22) {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER_ERROR]: textContent?.shortTextFields?.cardNumberInvalid })
    ccRef.current.scrollIntoView({
      behavior: "smooth"
    })
  }
}

// Date format has to be MM/YY
export const validateCardExpiryDate = (expiryDate, expiryRef, textContent, updatePaymentStore) => {
  if (expiryDate == null || expiryDate === '') {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateRequired })
    expiryRef.current.scrollIntoView({
      behavior: "smooth"
    })
    return
  }

  if (expiryDate.length === 5 && expiryDate.includes('/')) {
    const dateArr = expiryDate.split('/')

    const currentDate = new Date()
    let cardDate = ''
    let isDateInPast = false

    if (dateArr.length === 2) {
      const validMonth = Number(dateArr[0]) >= 1 && Number(dateArr[0]) <= 12
      const currentYear = Number(`${new Date().getFullYear()}`.slice(-2))
      const validYear = Number(dateArr[1]) >= currentYear

      if (validMonth && validYear) {
        cardDate = new Date(`20${dateArr[1]}`, dateArr[0], 1, 0, 0, 0, 0)
        isDateInPast = dateInPast(cardDate, currentDate)
      }

      if (isDateInPast) {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateExpired })
        expiryRef.current.scrollIntoView({
          behavior: "smooth"
        })
        return
      }

      if (validMonth && validYear) {
        // no error
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: '' })
        return
      }
      if (!validMonth) {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateValidRequired })
        expiryRef.current.scrollIntoView({
          behavior: "smooth"
        })
      }

      if (!validYear) {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateExpired })
        expiryRef.current.scrollIntoView({
          behavior: "smooth"
        })
      }
    } else {
      updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateInvalidFormat })
      expiryRef.current.scrollIntoView({
        behavior: "smooth"
      })
    }
  } else {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: textContent?.shortTextFields?.expiryDateInvalidFormat })
    expiryRef.current.scrollIntoView({
      behavior: "smooth"
    })
  }

}

const CreditDebitCard = ({ showErrors, textContent, validCreditCardContent }) => {
  const dispatch = useDispatch()
  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage,
    [APPLICATION_STATE.SERVICES]: brandServices
  } = useContext(ApplicationContext)

  const {
    [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER]: cardNumber,
    [PAYMENT_STATE.CC_INFO.EXPIRY_DATE]: expiryDate,
    [PAYMENT_STATE.CC_INFO.SECURITY_CODE]: securityCode,
    [PAYMENT_STATE.CC_INFO.POSTAL_CODE]: postalCode,
    [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER_ERROR]: errorCardMessage,
    [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: expiryDateErrorMessage,
    [PAYMENT_STATE.CC_INFO.SECURITY_CODE_ERROR]: securityCodeErrorMessage,
    [PAYMENT_STATE.CC_INFO.POSTAL_CODE_ERROR]: postalCodeErrorMessage,
    [PAYMENT_STATE.CC_INFO.CC_API_VALIDATION_ERROR]: ccApiValidationError,
    [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: billingAddress
  } = useSelector((state) => state.payAndReviewPage)

  const paymentState = useSelector((state) => state.payAndReviewPage)
  const pageContent = useSelector((state) => state.common?.content?.pageContent)
  const addressDebounceMs = getAddressSearchDebounceMs(pageContent, currentLanguage)

  const [cardType, setCardType] = useState('')
  const [detectedBrand, setDetectedBrand] = useState('')
  const [addressSuggestions, setAddressSuggestions] = useState([])

  const ccRef = useRef(null)
  const searchDebounceRef = useRef(null)
  const expiryRef = useRef(null)
  const postalRef = useRef(null)

  useEffect(() => () => clearTimeout(searchDebounceRef.current), [])

  const cardApiValidationError = useMemo(() => {
    if (!ccApiValidationError) return ""
    if (ccApiValidationError === CARD_VALIDATION_ERRORS.UNAUTHORIZED) {
      return textContent?.shortTextFields?.paymentSessionExpiredError
    }
    return textContent?.shortTextFields?.cannotProcessCreditCard
  }, [currentLanguage, ccApiValidationError])

  const updatePaymentStore = (payload) => {
    dispatch(updatePaymentStates(payload))
  }

  const handleCardNumberOnBlur = () => {
    validateCardNumber(cardNumber, ccRef, textContent, updatePaymentStore)
  }


  const handleExpiryDateOnBlur = () => {
    validateCardExpiryDate(expiryDate, expiryRef, textContent, updatePaymentStore)
  }

  const handleExpiryDateChange = (value) => {
    let checkValue = value
    checkValue = checkValue.replace('/', '')
    const isNumber = /^\d+$/.test(checkValue)

    if (isNumber || value === "") {
      const sanitizedValue = value.replace(/[\s/]/g, '')
      const expiryArr = sanitizedValue.match(/.{1,2}/g)
      if (expiryArr && expiryArr.length > 0) {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE]: expiryArr.join('/') })
      } else {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE]: sanitizedValue })
      }
    }
  }

  const handleCardNumberFocus = () => {
    if (errorCardMessage !== '') {
      updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER_ERROR]: '' })
    }
  }


  const handleCardNumberChange = (value) => {
    const sanitizedValue = value.replace(/[\s/]/g, '')
    const isNum = /^\d+$/.test(sanitizedValue) || sanitizedValue === ''
    let type

    if (isNum) {
      if (sanitizedValue.trim().length > 0) {
        type = CardType(value)
      }
      setDetectedBrand(type || '')
      if (type === 'amex') {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER]: formatCreditCardAmex(sanitizedValue) })
      } else {
        updatePaymentStore({ [PAYMENT_STATE.CC_INFO.CREDIT_CARD_NUMBER]: formatCreditCard(sanitizedValue) })
      }
    }
  }

  const handleSecurityCodeChange = (value) => {
    const sanitizedValue = value.replace(/[\s/]/g, '')
    const isNum = /^\d+$/.test(sanitizedValue) || sanitizedValue === ''

    if (isNum) {
      updatePaymentStore({ [PAYMENT_STATE.CC_INFO.SECURITY_CODE]: sanitizedValue })
    }
  }

  const handleExpiryDateFocus = () => {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.EXPIRY_DATE_ERROR]: '' })
  }

  const handleSecurityCardOnBlur = () => {
    validateSecurityCode(securityCode, cardNumber, expiryRef, textContent, updatePaymentStore)
  }
  const handleSecurityCodeFocus = () => {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.SECURITY_CODE_ERROR]: '' })
  }

  const handlePostalCodeFocus = () => {
    updatePaymentStore({ [PAYMENT_STATE.CC_INFO.POSTAL_CODE_ERROR]: '' })
  }

  const handlePostalBlur = () => {
    return PaymentChangeHandlers[PAYMENT_EVENTS.POSTAL_CODE].onBlur(
      paymentState,
      {
        postalCodeRequired: textContent?.shortTextFields?.postalZipRequired,
        postalCodeInvalid: textContent?.shortTextFields?.postalZipInvalid
      },
      updatePaymentStore
    )
  }

  useEffect(() => {
    if (showErrors) {
      if (PaymentChangeHandlers[PAYMENT_EVENTS.POSTAL_CODE].onBlur(
        paymentState,
        {
          postalCodeRequired: textContent?.shortTextFields?.postalZipRequired,
          postalCodeInvalid: textContent?.shortTextFields?.postalZipInvalid
        },
        updatePaymentStore
      )) {
        postalRef.current.scrollIntoView({
          behavior: "smooth"
        })
      }
      handleSecurityCardOnBlur()
      handleExpiryDateOnBlur()
      handleCardNumberOnBlur()
    }
  }, [showErrors])

  useEffect(() => {
    if (paymentState.creditCardNumberError) {
      handleCardNumberOnBlur()
    }
    if (paymentState.expiryDateError) {
      handleExpiryDateOnBlur()
    }
    if (paymentState.securityCodeError) {
      handleSecurityCardOnBlur()
    }
    if (paymentState.postalCodeError) {
      PaymentChangeHandlers[PAYMENT_EVENTS.POSTAL_CODE].onBlur(
        paymentState,
        {
          postalCodeRequired: textContent?.shortTextFields?.postalZipRequired,
          postalCodeInvalid: textContent?.shortTextFields?.postalZipInvalid
        },
        updatePaymentStore
      )
    }

  }, [currentLanguage])

  return (
    <div className="space-y-4">
      {cardApiValidationError && (
        <BodyError>
          {cardApiValidationError}
        </BodyError>
      )}
      <div ref={ccRef} className="w-full">
        <Input
          type="creditcard"
          id="cardNumber"
          label={textContent?.shortTextFields?.cardNumber}
          value={cardNumber}
          onBlur={handleCardNumberOnBlur}
          onChange={handleCardNumberChange}
          onFocus={handleCardNumberFocus}
          feedback={errorCardMessage}
          validation={errorCardMessage ? 'error' : undefined}
          maxLength={cardType && cardType === 'amex' ? 19 : 22}
          showCardBrands
          detectedCardBrand={detectedBrand}
          testId="payment-creditCard"
        />
      </div>
      {validCreditCardContent ? (
        <CardInfo tokens={{ marginBottom: 8 }}>
          <BodySmall>
            <MarkdownRenderer markdown={validCreditCardContent} paragraphClassForIndex={() => 'text-copy text-sm font-body leading-6'} />
          </BodySmall>
        </CardInfo>
      ) : null}
      <div ref={expiryRef} className="flex flex-col space-y-4 lg:flex-row lg:space-x-6 lg:space-y-0">
        <Input
          id="expiryDate"
          label={textContent?.shortTextFields?.expiryDate}
          value={expiryDate}
          onBlur={handleExpiryDateOnBlur}
          onChange={handleExpiryDateChange}
          onFocus={handleExpiryDateFocus}
          maxLength={5}
          feedback={expiryDateErrorMessage}
          validation={expiryDateErrorMessage ? 'error' : undefined}
          testId="payment-creditExpiry"
        />
        <Input
          id="securityCode"
          label={textContent?.shortTextFields?.securityCode}
          value={securityCode}
          onBlur={handleSecurityCardOnBlur}
          onChange={handleSecurityCodeChange}
          onFocus={handleSecurityCodeFocus}
          feedback={securityCodeErrorMessage}
          validation={securityCodeErrorMessage ? 'error' : undefined}
          maxLength={CardType(cardNumber) === 'amex' ? 4 : 3}
          testId="payment-creditSecurity"
        />
        <div ref={postalRef} className="flex-1">
          <Input
            id="postalCodeCard"
            label={textContent?.shortTextFields?.postalCode}
            value={postalCode}
            onBlur={handlePostalBlur}
            onChange={(value) => PaymentChangeHandlers[PAYMENT_EVENTS.POSTAL_CODE].onChange(value, updatePaymentStore)}
            onFocus={handlePostalCodeFocus}
            feedback={postalCodeErrorMessage}
            validation={postalCodeErrorMessage ? 'error' : undefined}
            maxLength={7}
            testId="payment-creditPostal"
          />
        </div>
      </div>
      <div className="w-full relative z-10">
        <Autocomplete
          label={textContent?.shortTextFields?.billingAddress}
          value={billingAddress}
          trailingIcon="LocationMap"
          trailingIconDecorative
          trailingIconStationary
          onChange={(value) => {
            updatePaymentStore({ [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: value })
            clearTimeout(searchDebounceRef.current)
            if (!value) { setAddressSuggestions([]); return }
            searchDebounceRef.current = setTimeout(async () => {
              const response = await brandServices.getAddress('find', value, null, currentLanguage?.toUpperCase(), 7)
              const results = response?.find || []
              setAddressSuggestions(
                results.map((item) => ({
                  id: item.Id,
                  label: item.Text ? `${item.Text}, ${item.Description}` : item.Description
                }))
              )
            }, addressDebounceMs)
          }}
          onSelect={async (id) => {
            clearTimeout(searchDebounceRef.current)
            const response = await brandServices.getAddress('retrieve', null, id, currentLanguage?.toLowerCase())
            const results = response?.retrieve || []
            const lang = currentLanguage?.toUpperCase() === 'EN' ? 'ENG' : 'FRE'
            const match = results.find((r) => r.Language === lang) || results[0]
            if (match) {
              const fullAddress = [match.Line1, match.City, match.ProvinceCode, match.PostalCode]
                .filter(Boolean)
                .join(', ')
              updatePaymentStore({ [PAYMENT_STATE.CC_INFO.BILLING_ADDRESS]: fullAddress })
            }
          }}
          options={addressSuggestions}
          testId="payment-billingAddress"
        />
      </div>
    </div>
  )
}

export default CreditDebitCard
